// Dependencies
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import 'sanitize.css/sanitize.css';
import 'sanitize.css/typography.css';
import 'sanitize.css/assets.css';
import 'sanitize.css/reduce-motion.css';
import 'sanitize.css/forms.css';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import { Script } from 'gatsby';

// Components
import Header from '../header/header';
import Menu from '../menu/menu';
import Footer from '../footer/footer';
import AwardsSlider from '../flexibleContent/awardsSlider/awardsSlider';
import Title from '../title/title';
import JsNotice from '../jsNotice/jsNotice';
import OldBrowserNotice from '../oldBrowserNotice/oldBrowserNotice';
import LocationPrompt from '../locationPrompt/locationPrompt';

// Contexts
import MenuContext from '../../contexts/menu';

// Styles
import LayoutStyles from './layout.styles';
import TypographyStyles from './typography.styles';
import ButtonStyles from './button.styles';
import UtilitiesStyles from './utilities.styles';
import OlarkStyles from './olark.styles';
import TableStyles from './table.styles';

export default function Layout({
  children,
  isFrontPage,
  pageDisplayOptions,
  title,
  userJourneyStage,
}) {
  const topSpacing = pageDisplayOptions?.topSpacing;

  const displayTitle = pageDisplayOptions?.displayTitle;
  const titleAlignment = pageDisplayOptions?.titleAlignment;

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, [setLoaded]);

  return (
    <div className="layout">
      <LayoutStyles />
      <TypographyStyles />
      <ButtonStyles />
      <UtilitiesStyles />
      <TableStyles />
      <OlarkStyles />

      <MenuContext.Consumer>
        {(value) => (
          <>
            <Header isFrontPage={isFrontPage} openMenu={value?.openMenu} />
            <Menu open={value?.open} closeMenu={value?.closeMenu} />
          </>
        )}
      </MenuContext.Consumer>

      <LazyMotion features={domAnimation}>
        <m.div
          className="main-wrapper"
          initial={loaded ? { opacity: 0 } : false}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            duration: 0.2,
          }}
          key="layout"
          onAnimationComplete={() => {
            if (loaded) {
              window.scrollTo(0, 0);
            }
          }}
        >
          <main
            className={`${isFrontPage ? 'front-page' : ''} ${
              topSpacing === true ? 'top-spacing' : ''
            }`}
          >
            {displayTitle && <Title alignment={titleAlignment}>{title}</Title>}

            {children}
          </main>
        </m.div>
      </LazyMotion>

      <AwardsSlider />
      <Footer />
      <LocationPrompt />
      <JsNotice />
      <OldBrowserNotice />

      {/* Send user journey stage as custom tag (Stage) to Microsoft Clarity */}
      {!!userJourneyStage && (
        <Script
          key="microsoftClarityUserJourneyStage"
          type="text/javascript"
          defer
          dangerouslySetInnerHTML={{
            __html: `if (typeof window !== 'undefined' && typeof window.clarity !== 'undefined') { window.clarity('set', 'Stage', '${userJourneyStage}'); }`,
          }}
        />
      )}
    </div>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  isFrontPage: PropTypes.bool,
  title: PropTypes.string,
  userJourneyStage: PropTypes.string,
  pageDisplayOptions: PropTypes.shape({
    topSpacing: PropTypes.bool,
    displayTitle: PropTypes.bool,
    titleAlignment: PropTypes.string,
  }),
};

Layout.defaultProps = {
  isFrontPage: false,
  title: '',
  userJourneyStage: null,
  pageDisplayOptions: {
    topSpacing: false,
  },
};
